import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef } from '@angular/material';
import { environment } from '@env/environment';

// Poll aggressively at the start so we capture log content before
// systeminfo_api gets killed by `docker compose down` (typically ~3-10s into
// the deploy). After that, slow down to a normal polling cadence.
const FAST_POLL_INTERVAL_MS = 400;
const SLOW_POLL_INTERVAL_MS = 1500;
const FAST_POLL_DURATION_MS = 20000;

@Component({
  selector: 'app-restart-progress-dialog',
  templateUrl: './restart-progress-dialog.component.html',
  styleUrls: ['./restart-progress-dialog.component.scss']
})
export class RestartProgressDialogComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('logPane', { static: false }) logPane: ElementRef;

  logText = '';
  status: 'polling' | 'unreachable' | 'idle' = 'polling';
  lastErrorMessage = '';

  private timer: any = null;
  private destroyed = false;
  private autoScroll = true;
  private pendingScroll = false;
  private startedAt = Date.now();

  constructor(
    private httpClient: HttpClient,
    private dialogRef: MatDialogRef<RestartProgressDialogComponent>
  ) { }

  ngOnInit() {
    this.poll();
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  ngAfterViewChecked() {
    if (this.pendingScroll && this.logPane && this.autoScroll) {
      this.logPane.nativeElement.scrollTop = this.logPane.nativeElement.scrollHeight;
      this.pendingScroll = false;
    }
  }

  onScroll() {
    if (!this.logPane) return;
    const el = this.logPane.nativeElement;
    // If user scrolls away from the bottom, stop auto-scrolling.
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    this.autoScroll = atBottom;
  }

  refreshPage() {
    const proceed = window.confirm(
      "WARNING: Refreshing the page will close this log view. " +
      "If the restart log does NOT show a clear success message (e.g. 'Done! Access CARGOEYE UI at ...'), " +
      "the restart may have failed and you will lose access to the UI. " +
      "A Cargo Eye expert admin would then be needed to recover the system. " +
      "Continue with refresh?"
    );
    if (!proceed) {
      return;
    }
    window.location.reload();
  }

  close() {
    this.dialogRef.close();
  }

  private poll() {
    if (this.destroyed) return;

    this.httpClient.get(
      `${environment.SYSTEMINFO_API_ENDPOINT}/restart-log`
    ).subscribe(
      (res: any) => {
        if (this.destroyed) return;
        this.status = 'polling';
        this.lastErrorMessage = '';
        const data = res && res.response;
        if (data && typeof data.log === 'string') {
          // Server always returns the full current log (capped). Replace, don't append.
          // This avoids any stale-prefix issues across restarts.
          if (this.logText !== data.log) {
            this.logText = data.log;
            this.pendingScroll = true;
          }
        }
        this.schedule();
      },
      (err: any) => {
        if (this.destroyed) return;
        this.status = 'unreachable';
        this.lastErrorMessage = (err && err.statusText) || 'Network error';
        this.schedule();
      }
    );
  }

  private schedule() {
    if (this.destroyed) return;
    const elapsed = Date.now() - this.startedAt;
    const interval = elapsed < FAST_POLL_DURATION_MS ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
    this.timer = setTimeout(() => this.poll(), interval);
  }
}
